import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import type { IkMatch } from "../lib/interventions";

/**
 * "Empfohlene Interventionen für diese Situation" — Andockung im geführten
 * Prozessschritt (/app/process-landscape, Bereich "Wie entsteht das Ergebnis?").
 * Ruft POST /v1/interventions/match; zeigt exakte Vorschläge mit Kurzbeschreibung,
 * "so geht's" (Wann sinnvoll / Ziel) und Warnhinweisen; verlinkt auf die Detailansicht.
 */
export default function InterventionEmpfehlungen({ situationText, autoRun = false }: { situationText: string; autoRun?: boolean }) {
  const api = useApi();
  const [text, setText] = useState(situationText || "");
  const m = useMutation({
    mutationFn: (t: string) => api.post<IkMatch>("/interventions/match", { situation_text: t, top_k: 5 }),
  });
  if (autoRun && situationText && !m.data && !m.isPending) m.mutate(situationText);

  return (
    <section className="sk-block ik-reco" aria-label="Empfohlene Interventionen">
      <div className="sk-block-head"><h2>Empfohlene Interventionen für diese Situation</h2></div>
      <div className="ik-reco-input">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
          placeholder="Situation in eigenen Worten beschreiben…" aria-label="Situation beschreiben" />
        <button className="ik-btn" onClick={() => m.mutate(text)} disabled={!text.trim() || m.isPending}>
          {m.isPending ? "sucht…" : "Interventionen finden"}
        </button>
      </div>

      {m.data && (
        <>
          <ol className="ik-reco-list">
            {m.data.results.map((r) => (
              <li key={r.code} className="ik-reco-item">
                <div className="ik-reco-top">
                  <Link className="ik-reco-name" to={`/app/interventionen?code=${encodeURIComponent(r.code)}`}>
                    {r.intervention.name}
                  </Link>
                  <span className="ik-reco-score" title="Trefferwert">{Math.round(r.score * 100) / 100}</span>
                </div>
                {r.intervention.kurzbeschreibung && <p className="ik-reco-desc">{r.intervention.kurzbeschreibung}</p>}
                {r.intervention.wann_sinnvoll && <p className="ik-reco-how"><strong>So geht's:</strong> {r.intervention.wann_sinnvoll}</p>}
                {r.warum?.length > 0 && <p className="ik-reco-why">Warum: {r.warum.join(" · ")}</p>}
                {r.intervention.nicht_einsetzen && (
                  <p className="ik-warn-inline">⛔ {r.intervention.nicht_einsetzen}</p>)}
                {(r.intervention.rechtlicher_vorbehalt || r.intervention.rechtlicher_vorbehalt_detail) && (
                  <p className="ik-legal-inline">§ {r.intervention.rechtlicher_vorbehalt || r.intervention.rechtlicher_vorbehalt_detail}</p>)}
              </li>
            ))}
          </ol>
          <p className="ik-source">Exakte Katalogeinträge ({m.data._meta?.katalog_version}) · Mandant {m.data.tenant} · verbatim aus Gold.</p>
        </>
      )}
      {m.isError && <p className="sk-honest">Empfehlung derzeit nicht verfügbar — der Katalog bleibt unter „Interventionskatalog" durchsuchbar.</p>}
    </section>
  );
}
